import { z } from 'zod';
/**
 * The graph an operator authors: conversation nodes with their own prompts and
 * tools, joined by events that fire when their criteria are met.
 *
 * Stored on its own row and read by the node-agent worker at the start of a
 * call. Dispatch carries which graph, never a copy of it. Field names mirror
 * what the worker parses (apps/livekit/node-agent/node_agent/graph/schema.py).
 */
export const NODE_AGENT_NAME = 'node-caller';
export const NODE_GRAPH_END = null;
/**
 * Compose the effective agent name from the base and an optional suffix.
 *
 * LiveKit routes an explicit-dispatch job by matching `agent_name` across
 * every worker connected to the project. In shared dev environments (many
 * developers plus a staging pod, all connected to the same LiveKit URL)
 * that produces a race — LiveKit load-balances a dispatch across every
 * worker registered under the same name.
 *
 * LiveKit's own guidance for this scenario is to append a per-developer
 * suffix in dev so each worker registers under a distinct name; production
 * leaves the suffix unset so replicas share one name and load-balance
 * normally. Both the worker (Python) and any code that dispatches
 * (`AgentDispatchClient.createDispatch(...)`) must derive the name the
 * same way from the same env var; this helper is the source of truth for
 * the TypeScript callers.
 *
 * See:
 * https://livekit.com/blog/avoiding-crosstalk-shared-dev-environments
 */
export function resolveNodeAgentName(suffix) {
    const trimmed = suffix?.trim();
    return trimmed ? `${NODE_AGENT_NAME}-${trimmed}` : NODE_AGENT_NAME;
}
export const eventDefSchema = z
    .object({
    /** Neutral-fact event name, e.g. "fit_confirmed" */
    name: z.string().min(1).max(64),
    /** Prose criteria the evaluator judges. Omitted for allOf conjunctions. */
    criteria: z.string().max(2000).optional(),
    /** Terminal events move the call: nextNode id, or null = end the call. */
    terminal: z.boolean().default(false),
    nextNode: z.string().nullable().optional(),
    /** Conjunction: fires mechanically once all named events have fired. */
    allOf: z.array(z.string()).min(2).optional(),
    /**
     * 'tool': fired deterministically by a tool result (never LLM-judged).
     * The event name must match an outcome the tool emits (TOOL_EMITTED_EVENTS).
     */
    source: z.enum(['llm', 'tool']).optional(),
})
    .refine(e => e.criteria != null || e.allOf != null || e.source === 'tool', {
    message: 'event needs criteria, allOf, or source: tool',
});
/** Selecting a skill as a step tool implies its underlying tool set. */
export const SKILL_TOOL_EXPANSIONS = {
    OWNER_MEETING_SCHEDULING: ['get_meeting_times', 'book_meeting', 'end_call'],
    SCHEDULING: ['get_meeting_times', 'book_meeting', 'end_call'],
};
/** Deterministic outcomes each tool can emit — the UI's "tool outcome" arrow options. */
export const TOOL_EMITTED_EVENTS = {
    book_meeting: [
        {
            name: 'meeting_booked',
            label: 'Meeting booked',
            description: 'Fires automatically the moment book_meeting succeeds.',
        },
    ],
    get_meeting_times: [],
    end_call: [],
};
export const nodeDefSchema = z.object({
    id: z.string().min(1).max(64),
    title: z.string().min(1).max(120),
    /** Only this node's instructions — the phase-scoped prompt. */
    prompt: z.string().min(1).max(20000),
    events: z.array(eventDefSchema).min(1),
    /** Tool names enabled while the call is in this node. */
    tools: z.array(z.string()).default([]),
    /** KBs whose content the admin UI compiles into the prompt at save time. */
    knowledgeBaseIds: z.array(z.string()).default([]),
});
export const nodeGraphSchema = z
    .object({
    version: z.literal(1),
    /** Persona/context prepended to every node's prompt. */
    basePersona: z.string().max(4000).default(''),
    /** Spoken as the first line of the call. */
    greeting: z.string().min(1).max(1000),
    start: z.string().min(1),
    nodes: z.array(nodeDefSchema).min(1),
})
    .superRefine((graph, ctx) => {
    const ids = new Set(graph.nodes.map(n => n.id));
    if (ids.size !== graph.nodes.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'duplicate node ids',
        });
    }
    if (!ids.has(graph.start)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `start node "${graph.start}" not found`,
        });
    }
    for (const node of graph.nodes) {
        const eventNames = new Set(node.events.map(e => e.name));
        for (const event of node.events) {
            if (event.terminal &&
                event.nextNode != null &&
                !ids.has(event.nextNode)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `node "${node.id}" event "${event.name}" points to missing node "${event.nextNode}"`,
                });
            }
            for (const member of event.allOf ?? []) {
                if (!eventNames.has(member)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `node "${node.id}" event "${event.name}" allOf references unknown event "${member}"`,
                    });
                }
            }
        }
    }
});
export function parseNodeGraph(value) {
    const result = nodeGraphSchema.safeParse(value);
    return result.success ? result.data : null;
}
export * from './graph.js';
export * from './equation.js';
export * from './equation-rules.js';
export * from './validate.js';
export * from './action-node.js';
export * from './form-node.js';
export * from './channel-capabilities.js';
export * from './start-conversation-node.js';
export * from './template-openings.js';
export * from './transfer-node.js';
export * from './enrich-for-dispatch.js';
export * from './owner-lead-template.js';
export * from './delinquency-template.js';
export * from './leasing-follow-up-template.js';
export * from './showing-scheduler-template.js';
export * from './pest-control-template.js';
export * from './receptionist-template.js';
export * from './lease-renewal-template.js';
export * from './new-owner-lead-template.js';
export * from './owner-intake-template.js';
export * from './translations.js';
export * from './graph-frontend-component-allowlist.js';
//# sourceMappingURL=index.js.map